/**
 * TaskCreateModal
 *
 * antd Modal wrapper around TaskFormContainer for quick task creation. CREATE-ONLY:
 * editing keeps the full page at /tasks/form/:id (the only full-form edit surface),
 * as does campaign-details task creation (that page mounts outside the /tasks route
 * tree, so the TaskProvider this form needs is not available there).
 *
 * Lives in forms/ (NOT the modals barrel): TaskFormContainer imports from
 * '../modals', so exporting this from that barrel would create a circular import.
 *
 * Keep it MOUNTED and toggle `visible` (don't conditionally mount) so the open/close
 * fade animation plays. `destroyOnClose` means the heavy form only mounts while open, and
 * `afterClose` resets the form instance so every open still starts fresh.
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form } from 'antd';
import { ExpandAltOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';

import TaskFormContainer from './TaskFormContainer';
import { setTaskFormDraft } from './taskFormDraft';
import useResponsiveTask from '../../hooks/useResponsiveTask';
import '../../styles/index.scss';

const TaskCreateModal = ({ visible, onClose, onSuccess, projectId }) => {
  const history = useHistory();
  const { isDesktop } = useResponsiveTask();
  // Modal is for laptop/desktop only; tablet and mobile use the full-page form.
  const useFullPage = !isDesktop;
  // Own the form instance so we can detect unsaved input before closing.
  const [form] = Form.useForm();
  // The container keeps a fresh draft snapshot here (form values + loaded option lists +
  // attachments) so a transition to the full-page form carries everything over — no data loss.
  const captureDraftRef = useRef(null);

  // Move to the full-page form, handing over the current draft so nothing entered is lost.
  const goToFullPage = () => {
    if (captureDraftRef.current) setTaskFormDraft(captureDraftRef.current);
    onClose();
    history.push(projectId ? `/tasks/form/new?projectId=${projectId}` : '/tasks/form/');
  };

  // The modal is laptop/desktop only — on tablet/mobile, switch to the full-page form. The draft
  // carries over, so there's nothing to lose and no warning is needed.
  useEffect(() => {
    if (visible && useFullPage) goToFullPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, useFullPage]);

  // Close (X / Cancel) DISCARDS the draft — warn first if there are unsaved entries.
  const handleClose = () => {
    if (form.isFieldsTouched()) {
      Modal.confirm({
        className: 'task-confirm-modal',
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Do you want to continue?',
        okText: 'Discard',
        cancelText: 'Keep editing',
        onOk: onClose,
      });
    } else {
      onClose();
    }
  };

  // On tablet/mobile we redirect (above) instead of rendering the modal.
  if (useFullPage) return null;

  return (
    <Modal
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingRight: 32, // leave room for the close (X) button at the top-right
          }}
        >
          <span>Create Task</span>
          <Button
            type="link"
            size="small"
            icon={<ExpandAltOutlined />}
            onClick={goToFullPage}
          >
            Open full page
          </Button>
        </div>
      }
      visible={visible}
      onCancel={handleClose}
      footer={null}
      width="min(1200px, 95vw)"
      centered
      // Custom opacity motion (see _modals.scss) — antd's default zoom wasn't animating the
      // open here. destroyOnClose keeps the heavy form mounted only while open; afterClose
      // resets the form instance for a fresh open.
      transitionName="task-modal-fade"
      destroyOnClose
      afterClose={() => {
        form.resetFields();
        captureDraftRef.current = null;
      }}
      // Only the X or Cancel button may close the modal — clicking the backdrop / Esc must not.
      maskClosable={false}
      keyboard={false}
      bodyStyle={{ maxHeight: '85vh', overflowY: 'auto' }}
      className="task-create-modal"
    >
      <TaskFormContainer
        isModal
        form={form}
        captureDraftRef={captureDraftRef}
        onClose={handleClose}
        onSuccess={onSuccess}
        projectId={projectId}
      />
    </Modal>
  );
};

TaskCreateModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  projectId: PropTypes.string,
};

TaskCreateModal.defaultProps = {
  onSuccess: undefined,
  projectId: undefined,
};

export default TaskCreateModal;
